import {Spin, Card} from 'antd';
import {useForm} from 'antd/es/form/Form';
import React, {Key, useEffect, useState, useCallback, useRef} from 'react';

import {ActionBar} from 'components/@common/widgets/action-bar';
import {useCustomTable} from 'components/@common/widgets/custom-table/custom-table';
import {AIS_ROUTES} from 'components/ais/ais.constants';
import {SimpleActionButton} from 'components/form/components';
import {ModalOpenerComponent} from 'components/modal-opener-component';
import {selectContextDataWithLookups} from 'modules/context/context-selector';
import {usePagination} from 'shared/hooks/use-pagination';
import {useUserRoleFunctions} from 'shared/hooks/use-user-role-functions';
import {useAppSelector} from 'store/config/hooks';
import {EUserRoleFunction} from 'store/slices/user-slice/user-slice-role-functions';

import {IRiskDocumentsDto, IPostRiskDocumentsArgs} from '../../api/types';
import {useRisksDocumentsColumns} from '../../hooks/use-risks-documents-columns';
import {TRisksDocumentsFormRef} from '../../model/types';
import {RisksDocumentsService} from '../../services';
import {RisksDocumentsForm} from '../form/risks-documents-form';

export const Page = () => {
    const [tableData, setTableData] = useState<IRiskDocumentsDto[]>();

    const [selectedRowKeys, setSelectedRowKeys] = useState<Key[]>([]);

    const [isLoadingTableData, setIsLoadingTableData] = useState<boolean>(false);

    const [totalRows, setTotalRows] = useState<number>(0);

    const [currentPage, setCurrentPage] = useState<number>(1);

    const {pageSize, handleChange} = usePagination({pageDefault: 1, pageSizeDefault: 10});

    const {hasUserRoleFunction} = useUserRoleFunctions({sections: [AIS_ROUTES.RISKS_DOCUMENTS_MAPPING]});

    const isCreateAllowed = hasUserRoleFunction(EUserRoleFunction.CREATE);

    const isDeleteAllowed = hasUserRoleFunction(EUserRoleFunction.DELETE);

    const [form] = useForm();

    const riskDocumentsFormRef = useRef<TRisksDocumentsFormRef>(null);

    const [createFormKey, setCreateFormKey] = useState(0);

    const [isEmpty, setIsEmpty] = useState<boolean>(false);

    const contextDataWithLookups = useAppSelector(selectContextDataWithLookups);

    const {organizationId} = contextDataWithLookups;

    const fetchData = useCallback(async () => {
        if (!organizationId) return;
        try {
            setIsLoadingTableData(true);
            const {data} = await RisksDocumentsService.get({
                paginationPageSize: pageSize,
                paginationCurrent: currentPage,
                organizationId
            });
            setTableData(data.dataTable);
            setTotalRows(data.recordsTotal);
            setIsLoadingTableData(false);
        } catch (e) {
            console.error('Ошибка получения записей: ', e);
        }
    }, [pageSize, currentPage, organizationId]);

    const columns = useRisksDocumentsColumns(tableData, fetchData);

    useEffect(() => {
        fetchData();
    }, [pageSize, currentPage, organizationId]);

    const {customTableJSX} = useCustomTable({
        rowKey: 'id',
        pagination: {
            position: ['bottomLeft'],
            total: (() => totalRows)(),
            pageSize,
            onChange: (value: number) => {
                handleChange(value);
                setCurrentPage(value);
            },
        },
        rowSelection: {selectedRowKeys, onChange: (selectedTableRowKeys) => setSelectedRowKeys(selectedTableRowKeys)},
        className: 'mt-5',
        dataSource: tableData,
        bordered: true,
        columns,
    });

    const handleDeleteRows = useCallback(async (rowsIds: Key[]) => {
        try {
            await RisksDocumentsService.delete({rowsIds});
            setSelectedRowKeys([]);
            await fetchData();
        } catch (e) {
            console.error(e);
        }
    }, []);

    const handleFinish = useCallback(async (values: IPostRiskDocumentsArgs) => {
        try {
            await RisksDocumentsService.post(values);

            await fetchData();
        } catch (e) {
            console.error('Ошибка обновления/создания записи: ', e);
        }
    }, []);

    return (
        <Card title="Настройка правил связи рисков с документами">
            <ActionBar>
                <section className="d-flex justify-content-between w-100">
                    <div className="d-flex gap-2">
                        {isCreateAllowed && (
                            <ModalOpenerComponent
                                component={
                                    <SimpleActionButton
                                        type="primary"
                                        icon="PlusCircleOutlined"
                                        title="Создать"
                                        onClick={() => {}}
                                    />
                                }
                                controlsStyle={{marginTop: '20px'}}
                                modalProps={{title: 'Настройка правила связи', width: 1600}}
                                controlLabels={{save: 'Сохранить'}}
                                disabledControls={{save: isEmpty}}
                                handleSave={async (setIsModalOpen) => {
                                    try {
                                        await form.validateFields();
                                        const isValid = await riskDocumentsFormRef.current?.validate();
                                        if (isValid) {
                                            form.submit();
                                            setIsModalOpen(false);
                                        }
                                    } catch (e) {
                                        console.error(e);
                                    }
                                }}
                                shouldConfirm={() =>
                                    form.isFieldsTouched() || !!riskDocumentsFormRef.current?.isRulesDirty()
                                }
                                handleCancel={(setIsModalOpen) => {
                                    form.resetFields();
                                    setIsModalOpen(false);
                                }}
                                afterModalClose={() => {
                                    form.resetFields();
                                    setCreateFormKey((k) => k + 1);
                                }}
                            >
                                <RisksDocumentsForm
                                    key={createFormKey}
                                    formInstance={form}
                                    onFinish={handleFinish}
                                    ref={riskDocumentsFormRef}
                                    setIsEmpty={setIsEmpty}
                                />
                            </ModalOpenerComponent>
                        )}
                        {isDeleteAllowed && (
                            <ModalOpenerComponent
                                component={
                                    <SimpleActionButton
                                        disabled={selectedRowKeys?.length === 0}
                                        type="primary"
                                        className="button-critic"
                                        icon="TrashXOutlined"
                                        title="Удалить выбранное"
                                    />
                                }
                                controlsStyle={{marginTop: '20px'}}
                                modalProps={{title: 'Удаление'}}
                                controlLabels={{save: 'Удалить'}}
                                handleSave={(setIsModalOpen) => {
                                    if (handleDeleteRows && selectedRowKeys) handleDeleteRows(selectedRowKeys);
                                    setIsModalOpen(false);
                                }}
                            >
                                Вы уверены, что хотите удалить правило?
                            </ModalOpenerComponent>
                        )}
                    </div>
                </section>
            </ActionBar>
            <Spin spinning={isLoadingTableData}>{customTableJSX}</Spin>
        </Card>
    );
};
